'use client';
import React from 'react';
import { createSymbol } from '@benzinga/autocomplete-manager';
import { SessionContext } from '@benzinga/session-context';
import { QuoteProtos, ScannerManager } from '@benzinga/scanner-manager';
import { DEFAULT_SCANNER_CONFIG } from '@benzinga/scanner-config-manager';
import Hooks from '@benzinga/hooks';
import { createSymbolSearchItem, ExtendedSearchItem } from '@benzinga/search-modules';
import { WidgetId } from '@benzinga/widget-tools';
import { LocalStorageKey } from '../../entities';
import { getLocalStorageItem, setLocalStorageItem } from '@benzinga/frontend-utils';
import { usePermission } from '@benzinga/user-context';

interface SearchContextProps {
  shouldDisplayDefaultTickers?: () => boolean;
  shouldDisplayRecentTickers?: () => boolean;
  addRecentModuleItem: (autocompleteItem: ExtendedSearchItem) => void;
  getRecentModuleItem: (widgetId?: WidgetId | undefined) => ExtendedSearchItem[];
  getScannerAutocompleteItem: () => ExtendedSearchItem[];
}

type SearchProps = Omit<SearchContextProps, 'getResentTickers' | 'onTickerSelected'> & {
  onRecentModuleItemChange: (item: ExtendedSearchItem) => void;
};

const getRecentModuleItem = (widgetId: WidgetId | undefined): ExtendedSearchItem[] => {
  const recentItems = getLocalStorageItem(LocalStorageKey.recentAutocompleteQuotes);
  return Array.isArray(recentItems) && widgetId
    ? //pins the last used module in each of those module sections
      recentItems.filter((item: ExtendedSearchItem) => widgetId === item.widgetId)
    : [];
};

const MAX_NUMBER_OF_ITEMS_TO_REMEMBER = 40;

const addRecentModuleItem = (item: ExtendedSearchItem): void => {
  const recentItems = getRecentModuleItem(item.widgetId);
  const existingIndex = recentItems.findIndex(i => i.id === item.id && i.moduleId === item.moduleId);
  if (existingIndex !== -1) {
    recentItems.splice(existingIndex, 1);
  }
  recentItems.unshift(item);
  setLocalStorageItem(LocalStorageKey.recentAutocompleteQuotes, recentItems.slice(0, MAX_NUMBER_OF_ITEMS_TO_REMEMBER));
};

export const SearchContext = React.createContext<SearchContextProps>({
  addRecentModuleItem,
  getRecentModuleItem,
  getScannerAutocompleteItem: () => [],
  shouldDisplayDefaultTickers: () => true,
  shouldDisplayRecentTickers: () => true,
});

export type SearchContextType = React.ContextType<typeof SearchContext>;

export const SearchContextProvider: React.FC<React.PropsWithChildren<Partial<SearchProps>>> = React.memo(props => {
  const addRecentTickerHelper = React.useCallback(
    (item: ExtendedSearchItem) => {
      addRecentModuleItem(item);
      const onRecentModuleItemChange = props.onRecentModuleItemChange;
      onRecentModuleItemChange?.(item);
    },
    [props.onRecentModuleItemChange],
  );

  const session = React.useContext(SessionContext);
  const scannerManager = session.getManager(ScannerManager);
  const scannerAutocompleteItems = React.useRef<ExtendedSearchItem[]>([]);
  const hasScannerAccess = usePermission('bzpro/widget/use', 'scanner');

  Hooks.useSubscriber(hasScannerAccess ? scannerManager.getFeed(DEFAULT_SCANNER_CONFIG) : undefined, event => {
    switch (event.type) {
      case 'data_update':
        scannerAutocompleteItems.current = event.rows.flatMap((ticker: QuoteProtos.IQuote) => {
          if (!ticker.symbol || !ticker.name) return [];
          const symItem = createSymbol({
            name: ticker.name,
            symbol: ticker.symbol,
            type: 'symbol',
          });
          return [createSymbolSearchItem(symItem.tagId, symItem.label)];
        });
        break;
    }
  });

  const getScannerAutocompleteItem = React.useCallback(
    () => scannerAutocompleteItems.current,
    [scannerAutocompleteItems],
  );

  return (
    <SearchContext.Provider
      value={{
        addRecentModuleItem: addRecentTickerHelper,
        getRecentModuleItem,
        getScannerAutocompleteItem,
        shouldDisplayDefaultTickers: props.shouldDisplayDefaultTickers ?? (() => true),
        shouldDisplayRecentTickers: props.shouldDisplayRecentTickers ?? (() => true),
      }}
    >
      {props.children}
    </SearchContext.Provider>
  );
});

SearchContextProvider.displayName = 'SearchContextProvider';
